import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import UserRoleBadge from './UserRoleBadge';

const UserTable = ({ users = [], loading = false, onEdit, onDeactivate }) => {
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      valueGetter: (value) => value || '-',
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 130,
      renderCell: (params) => <UserRoleBadge role={params.value} />,
    },
    {
      field: 'consultancyId',
      headerName: 'Consultancy',
      width: 160,
      valueGetter: (value, row) => (row.role === 'consultancy' ? (value?.name || '-') : ''),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const isActive = params.value;
        const color = isActive ? '#00D27A' : '#748194';
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: alpha(color, 0.12),
              color: color,
              fontWeight: 600,
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
            }}
          >
            {isActive ? 'Active' : 'Inactive'}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit && onEdit(params.row);
              }}
              sx={{
                color: '#748194',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: '#2C7BE5',
                  backgroundColor: alpha('#2C7BE5', 0.08),
                },
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.isActive ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeactivate && onDeactivate(params.row);
              }}
              sx={{
                color: params.row.isActive ? '#E63757' : '#00D27A',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: params.row.isActive
                    ? alpha('#E63757', 0.08)
                    : alpha('#00D27A', 0.08),
                },
              }}
            >
              <BlockOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = users.map((user) => ({
    id: user._id,
    ...user,
  }));

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          border: 'none',
          fontFamily: 'Open Sans, sans-serif',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F9FAFD',
            borderBottom: '1px solid #E3E6ED',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            color: '#344050',
            fontSize: '0.8rem',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #F0F2F7',
            color: '#344050',
            fontSize: '0.85rem',
            transition: 'background-color 0.2s ease',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#F9FAFD',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #E3E6ED',
          },
        }}
      />
    </Box>
  );
};

export default UserTable;
